import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal,
  SimpleChanges,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Componente reutilizable para campos opcionales
 * Encapsula la lógica de mostrar/ocultar, manejo de FormControl y botones de agregar/remover
 * El contenido del campo es definido por proyección (ng-content)
 */
@Component({
  selector: 'app-optional-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './optional-field.component.html',
  styleUrl: './optional-field.component.scss'
})
export class OptionalFieldComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  /**
   * FormControl(s) a limpiar cuando se remueve el campo
   * Puede ser un AbstractControl simple, array de AbstractControls, o null
   */
  @Input() formControls: AbstractControl | AbstractControl[] | null = null;

  /**
   * Label del botón para agregar el campo
   */
  @Input() triggerLabel: string = 'Agregar campo';

  /**
   * Tooltip del botón para remover el campo
   */
  @Input() removeTooltip: string = 'Remover';

  // Signal reactivo para controlar visibilidad
  isVisible = signal(false);

  private destroy$ = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initializeVisibility();
    this.setupValueChangeListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formControls']) {
      this.initializeVisibility();
      this.setupValueChangeListeners();
    }
  }

  ngAfterViewInit(): void {
    // Verificar nuevamente la visibilidad después de que la vista se haya inicializado
    this.initializeVisibility();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura listeners para cambios de valor en los FormControls
   * Solo actualiza visibilidad cuando el control pasa de vacío a tener valor
   */
  private setupValueChangeListeners(): void {
    if (!this.formControls) {
      return;
    }

    const controls = Array.isArray(this.formControls)
      ? this.formControls
      : [this.formControls];

    controls.forEach(control => {
      if (control && control.valueChanges) {
        control.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateVisibilityIfNeeded();
            this.cdr.markForCheck();
          });
      }
    });
  }

  /**
   * Actualiza la visibilidad cuando se pasa de vacío a tener valor
   */
  private updateVisibilityIfNeeded(): void {
    if (!this.formControls) {
      return;
    }

    const controls = Array.isArray(this.formControls)
      ? this.formControls
      : [this.formControls];

    // Si algún control tiene valor ahora, mostrar el campo
    const hasValue = controls.some(control => this.hasSignificantValue(control));
    
    // Solo actualizar si cambia de invisible a visible
    if (hasValue && !this.isVisible()) {
      this.isVisible.set(true);
    }
  }

  /**
   * Verifica si el control tiene un valor significativo
   */
  private hasSignificantValue(control: AbstractControl | null): boolean {
    if (!control) return false;
    
    const value = control.value;
    
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && value === 0) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    
    return true;
  }

  /**
   * Inicializa la visibilidad basada en valores iniciales
   */
  private initializeVisibility(): void {
    if (!this.formControls) {
      this.isVisible.set(false);
      return;
    }

    const controls = Array.isArray(this.formControls)
      ? this.formControls
      : [this.formControls];

    // Si algún control tiene valor inicial, mostrar el campo
    const hasInitialValue = controls.some(control => this.hasSignificantValue(control));
    this.isVisible.set(hasInitialValue);
  }

  onAdd(): void {
    this.isVisible.set(true);
  }

  onRemove(): void {
    this.clearFormControls();
    this.isVisible.set(false);
  }

  /**
   * Limpia el valor de los FormControls
   */
  private clearFormControls(): void {
    if (!this.formControls) {
      return;
    }

    const controls = Array.isArray(this.formControls) 
      ? this.formControls 
      : [this.formControls];

    controls.forEach(control => {
      if (control) {
        control.reset();
      }
    });
  }
}
